import React from 'react';
import { css, keyframes } from '@emotion/react';
import { StaticImage } from 'gatsby-plugin-image';
import { i18n_ayurveda } from '@yi/i18n/phrases/i18n_ayurveda';
import { i18n_breathwork } from '@yi/i18n/phrases/i18n_breathwork';
import { i18n_create_your_daily_ritual } from '@yi/i18n/phrases/i18n_create_your_daily_ritual';
import { i18n_meditation } from '@yi/i18n/phrases/i18n_meditation';
import { i18n_yoga } from '@yi/i18n/phrases/i18n_yoga';

import { getCssResponsiveImageSet } from '~/src/common/image';
import { useLocale } from '~/src/components/LocaleProvider';
import { breakpoints, fontWeight } from '~/src/styles';
import { colors, paddingVariables, typography } from '~/src/styles/design-system';
import { resetStyles } from '~/src/styles/reset-styles-mixins';

import { commonStyles } from './common-styles';

const YOGA_IMG_SRC =
  'https://images.ctfassets.net/p0sybd6jir6r/5wCnMiONfFKlFQB5YkrxII/ac4630d8525211f3d3c50d69cb232d58/Warrior_1_Pose.png';

const MEDITATION_IMG_SRC =
  'https://images.ctfassets.net/p0sybd6jir6r/6Kin17QjGUMloQxjKtIhUL/390011106496d776eced856f97497ae6/1058823410.jpg';
const BREATHWORK_IMG_SRC =
  'https://images.ctfassets.net/p0sybd6jir6r/670FEYDBCkXXeRJcDMg4Ne/a73aff4c937540381fd88f345228a367/breathwork-Recolorized2.png';

const AYURVEDIC_PRACTICES_IMG_SRC =
  'https://images.ctfassets.net/p0sybd6jir6r/34tn5aDUvQkJvpPzlXGYDr/3b10cb0ab641dac7741873226cc7100c/AdobeStock_437888256.jpeg';

const changeTextColorAnimation = keyframes`
    0% {color: var(--Slide-Text-Color); font-weight: var(--Slide-Text-Font-Weight) ; }
    2% {color: ${colors.Primary}; font-weight: var(--Active-Slide-Text-Font-Weight); animation-timing-function: ease-in-out; }
    23% { color:${colors.Primary}; font-weight:var(--Active-Slide-Text-Font-Weight); animation-timing-function: ease-in-out;}
    25% { color:var(--Slide-Text-Color); font-weight: var(--Slide-Text-Font-Weight); animation-timing-function: ease-in-out;}
    100% { color:var(--Slide-Text-Color); font-weight: var(--Slide-Text-Font-Weight) ;}
`;
const imageAnimation = keyframes`
    0% { opacity: 0; animation-timing-function: ease-in-out;}
    3% { opacity: 1; animation-timing-function: ease-in-out; }
    23% { opacity: 1;  animation-timing-function: ease-in-out;}
    30% { opacity: 0 }
    100% { opacity: 0 }
`;

const styles = {
  container: css({
    '--Animation-Frame-Time': '3s',
    '--Animation-Frames': '4',
    '--Slide-Text-Font-Weight': fontWeight.semiBold,
    '--Slide-Text-Color': 'var(--Grey-Text, #585858)',
    '--Active-Slide-Text-Font-Weight': fontWeight.semiBold,
    display: 'grid',
    gridTemplateAreas: "'title' '.' 'img'",
    gridTemplateRows: `auto ${paddingVariables.p56} auto`,
    paddingBottom: 'var(--Section-Padding)',
    [breakpoints.minTablet]: {
      gridTemplateAreas: "'title' '.' 'slides-names' '.' 'img'",
      gridTemplateRows: `auto ${paddingVariables.p24} auto ${paddingVariables.p80} auto`,
      '--Slide-Text-Font-Weight': fontWeight.medium,
      '--Active-Slide-Text-Font-Weight': fontWeight.bold,
      '--Slide-Text-Color': 'var(--Grey-Text)',
    },
    [breakpoints.minDesktop]: {
      gridTemplateAreas: "'img' 'img' 'img' 'img'",
      gridTemplateRows: `${paddingVariables.p56} auto ${paddingVariables.p24} 1fr`,
    },
  }),
  heading: css(typography.heading2, {
    textAlign: 'center',
    gridArea: 'title',
    color: 'var(--Dark-Text, #323439)',
    [breakpoints.minDesktop]: {
      gridArea: 'unset',
      gridRowStart: '2',
      gridColumnStart: '1',
      zIndex: '10',
    },
  }),
  slideItem: css({
    width: '100%',
  }),
  img: css({
    width: '100%',
  }),
  slideNamesContainer: css(resetStyles.ul, commonStyles.contentPadding, typography.bodyText, {
    zIndex: '10',
    alignSelf: 'start',
    marginTop: '8px',
    gridArea: 'img',
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: fontWeight.semiBold,
    lineHeight: '35px',
    color: 'var(--Grey-Text, #585858)',
    [breakpoints.minTablet]: {
      marginTop: '0px',
      gridArea: 'slides-names',
      fontSize: '22px',
      fontWeight: fontWeight.medium,
      color: 'var(--Grey-Text, #585858)',
    },
    [breakpoints.minDesktop]: {
      gridArea: 'unset',
      gridRowStart: '4',
      gridColumnStart: '1',
      zIndex: '10',
      fontSize: '28px',
      color: 'var(--Grey-Text, #585858)',
    },
    '& > div::after': {
      content: 'attr(data-text) / ""',
      height: 0,
      visibility: 'hidden',
      overflow: 'hidden',
      userSelect: 'none',
      pointerEvents: 'none',
      fontWeight: 'var(--Active-Slide-Text-Font-Weight)',
    },
    '& > div': {
      animation: `${changeTextColorAnimation} calc(var(--Animation-Frame-Time) * var(--Animation-Frames)) ease infinite`,
      whiteSpace: 'nowrap',
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    '& > div:nth-of-type(1)': {
      animationDelay: 'calc(var(--Animation-Frame-Time) * 0)',
    },
    '& > div:nth-of-type(2)': {
      animationDelay: 'calc(var(--Animation-Frame-Time) * 1)',
    },
    '& > div:nth-of-type(3)': {
      animationDelay: 'calc(var(--Animation-Frame-Time) * 2)',
    },
    '& > div:nth-of-type(4)': {
      animationDelay: 'calc(var(--Animation-Frame-Time) * 3)',
    },
  }),
  textSeparator: css({
    color: 'var(--Slide-Text-Color)',
    fontWeight: 'var(--Slide-Text-Font-Weight)',
    margin: '0 6px',
  }),
  slides: css({
    width: '246px',
    height: '246px',
    gridRowStart: '3',
    gridRowEnd: '5',
    gridColumnStart: '1',
    alignSelf: 'center',
    justifySelf: 'center',
    [breakpoints.minTablet]: {
      width: '339px',
      height: '339px',
      gridRowStart: '5',
      gridRowEnd: '6',
    },
    [breakpoints.minDesktop]: {
      gridRowStart: '4',
      width: '557px',
      height: '557px',
    },
    borderRadius: '300px',
    overflow: 'hidden',
    position: 'relative',
    '& > div': {
      zIndex: 10,
      animation: `${imageAnimation} calc(var(--Animation-Frame-Time) * var(--Animation-Frames)) ease infinite`,
      width: '100%',
      height: '100%',
      position: 'absolute',
      top: 0,
      borderRadius: '300px',
    },
    '& > div:nth-of-type(1)': {
      animationDelay: 'calc(var(--Animation-Frame-Time) * 0)',
      opacity: 0,
      backgroundImage: `${getCssResponsiveImageSet({
        src: YOGA_IMG_SRC,
        width: 246 * 1.5,
      })}`,
      backgroundColor: 'lightgray',
      backgroundPosition: '-51.301px -67px',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '141.586% 129.66%',
      [breakpoints.minTablet]: {
        backgroundImage: `${getCssResponsiveImageSet({
          src: YOGA_IMG_SRC,
          width: 339 * 1.5,
        })}`,
        backgroundPosition: '-70.696px -92.329px',
      },
      [breakpoints.minDesktop]: {
        backgroundPosition: '-116.158px -151.159px',
        backgroundImage: `${getCssResponsiveImageSet({
          src: YOGA_IMG_SRC,
          width: 557 * 1.5,
        })}`,
      },
    },
    '& > div:nth-of-type(2)': {
      animationDelay: 'calc(var(--Animation-Frame-Time) * 1)',
      opacity: 0,
      backgroundImage: `${getCssResponsiveImageSet({
        src: MEDITATION_IMG_SRC,
        width: 246 * 1.5,
      })}`,
      backgroundPosition: '-16.721px -105px',
      backgroundSize: '109.643% 143.895%',
      backgroundColor: 'lightgray',
      backgroundRepeat: 'no-repeat',
      [breakpoints.minTablet]: {
        backgroundImage: `${getCssResponsiveImageSet({
          src: MEDITATION_IMG_SRC,
          width: 339 * 1.5,
        })}`,
        backgroundPosition: '-16.721px -147px',
      },
      [breakpoints.minDesktop]: {
        backgroundImage: `${getCssResponsiveImageSet({
          src: MEDITATION_IMG_SRC,
          width: 557 * 1.5,
        })}`,
        backgroundPosition: '-16.721px -220px',
      },
    },
    '& > div:nth-of-type(3)': {
      animationDelay: 'calc(var(--Animation-Frame-Time) * 2)',
      opacity: 0,
      backgroundImage: ` ${getCssResponsiveImageSet({
        src: BREATHWORK_IMG_SRC,
        width: 246 * 1.2,
      })}`,
      backgroundPosition: '-27.044px -16.208px',
      backgroundSize: '110.181% 106.673%',
      backgroundColor: 'lightgray',
      backgroundRepeat: 'no-repeat',
      [breakpoints.minTablet]: {
        backgroundImage: ` ${getCssResponsiveImageSet({
          src: BREATHWORK_IMG_SRC,
          width: 339 * 1.2,
        })}`,
        backgroundPosition: '-27.044px -23px',
      },
      [breakpoints.minDesktop]: {
        backgroundImage: ` ${getCssResponsiveImageSet({
          src: BREATHWORK_IMG_SRC,
          width: 557 * 1.2,
        })}`,
        backgroundPosition: '-64.044px -41px',
        backgroundSize: '111%',
      },
    },
    '& > div:nth-of-type(4)': {
      animationDelay: 'calc(var(--Animation-Frame-Time) * 3)',
      opacity: 0,
      backgroundImage: `${getCssResponsiveImageSet({
        src: AYURVEDIC_PRACTICES_IMG_SRC,
        width: 246 * 1.9,
      })}`,
      backgroundPosition: '-42.477px -1.024px',
      backgroundSize: '193.284% 128.871%',
      backgroundColor: 'lightgray',
      backgroundRepeat: 'no-repeat',
      [breakpoints.minTablet]: {
        backgroundPosition: '-58px -1.024px',

        backgroundImage: ` ${getCssResponsiveImageSet({
          src: AYURVEDIC_PRACTICES_IMG_SRC,
          width: 339 * 1.9,
        })}`,
      },
      [breakpoints.minDesktop]: {
        backgroundPosition: '-85px -1.024px',
        backgroundSize: '188%',
        backgroundImage: ` ${getCssResponsiveImageSet({
          src: AYURVEDIC_PRACTICES_IMG_SRC,
          width: 557 * 1.9,
        })}`,
      },
    },
    '& > div:nth-of-type(5)': {
      zIndex: 0,
      animation: 'unset',
      opacity: 1,
      background: 'var(--Pure-White)',
    },
  }),
  backgroundContainer: css({
    gridArea: 'img',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: 'var(--Border-Radius)',
    position: 'relative',
    justifyContent: 'center',
    overflow: 'hidden',
    height: '640px',
    [breakpoints.minTablet]: {
      height: '740px',
    },
    [breakpoints.minDesktop]: {
      height: '988px',
      borderRadius: 0,
    },
  }),
  backgroundImg: css({
    objectFit: 'cover',
    objectPosition: '64% 0%',
    width: '100%',
    height: '100%',
  }),
};

const Italic: React.FC<{ text: string }> = ({ text }) => <i>{text}</i>;

export const CreateYouDialyRitual: React.FC = () => {
  const { locale } = useLocale();

  return (
    <section css={styles.container}>
      <h1 css={styles.heading}>{i18n_create_your_daily_ritual(locale, { transformFn: Italic })}</h1>
      <h2 css={styles.slideNamesContainer}>
        <div data-text={i18n_yoga(locale)}>{i18n_yoga(locale)}</div>
        <span css={styles.textSeparator}>/</span>
        <div data-text={i18n_meditation(locale)}>{i18n_meditation(locale)}</div>
        <span css={styles.textSeparator}>/</span>
        <div data-text={i18n_breathwork(locale)}>{i18n_breathwork(locale)}</div>
        <span css={styles.textSeparator}>/</span>
        <div data-text={i18n_ayurveda(locale)}>{i18n_ayurveda(locale)}</div>
      </h2>
      <div css={styles.backgroundContainer}>
        <StaticImage
          formats={['auto', 'webp']}
          src="./CreateYouDialyRitual_background.jpeg"
          placeholder="blurred"
          alt="mountains background"
          loading="lazy"
          css={styles.backgroundImg}
          objectPosition="50% 0%"
        />
      </div>
      <div css={styles.slides}>
        <div />
        <div />
        <div />
        <div />
        <div />
      </div>
    </section>
  );
};
